import {
  CurrentUserTeacherUnreadCountsQueryQuery,
  CurrentUserUnreadCountsQueryQuery,
} from "@outschool/gql-frontend-generated";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import {
  currentUserTeacherUnreadCountsQuery,
  currentUserUnreadCountsQuery,
  useSession,
} from "@outschool/ui-auth";
import { useLocation } from "react-router";

import * as User from "../../shared/User";
import { adultConversationUserModeForPath } from "../components/conversations/conversationRouteHelpers";
import { UserMode, useUserMode } from "../lib/UserMode";
import { useTeacherUnifiedMessaging } from "./useTeacherUnifiedMessaging";

export type CurrentUserUnreadCounts = {
  uid: string;
  unreadMessageThreadsCount?: number;
  unreadPrivateClassMessageThreadsCount?: number;
  unreadMessageThreadGroupsCount?: number;
  unreadAssignmentCommentThreadsCount?: number;
};

function useTeacherInboxUnreadCounts() {
  const { isLeader } = useSession();
  const userMode = useUserMode();
  const { pathname } = useLocation();
  const isTeacherUnifiedMessagingEnabled = useTeacherUnifiedMessaging();
  const inboxUserMode = adultConversationUserModeForPath(pathname, userMode);
  return Boolean(
    isLeader &&
      inboxUserMode === UserMode.Educator &&
      isTeacherUnifiedMessagingEnabled,
  );
}

export function useCurrentUserUnreadCountsQuery() {
  return useTeacherInboxUnreadCounts()
    ? currentUserTeacherUnreadCountsQuery
    : currentUserUnreadCountsQuery;
}

export function useCurrentUserUnreadCounts(): CurrentUserUnreadCounts | null {
  const { isLoggedIn } = useSession();
  const useTeacherCounts = useTeacherInboxUnreadCounts();

  const { data: parentData } =
    useQueryWithPreviousData<CurrentUserUnreadCountsQueryQuery>(
      currentUserUnreadCountsQuery,
      {
        skip: !isLoggedIn || useTeacherCounts,
        fetchPolicy: "cache-and-network",
      },
    );
  const { data: teacherData } =
    useQueryWithPreviousData<CurrentUserTeacherUnreadCountsQueryQuery>(
      currentUserTeacherUnreadCountsQuery,
      {
        skip: !isLoggedIn || !useTeacherCounts,
        fetchPolicy: "cache-and-network",
      },
    );

  return (useTeacherCounts ? teacherData : parentData)?.currentUser ?? null;
}

export function useUnreadConversationsBadgeCount() {
  const useTeacherCounts = useTeacherInboxUnreadCounts();
  const unreadUser = useCurrentUserUnreadCounts();
  return User.unreadConversationsCount(unreadUser, {
    includeAssignmentCommentThreads: useTeacherCounts,
    useMessageThreadGroupsCount: useTeacherCounts,
  });
}
